import 'phaser'
import { IJobCreator } from '../behavior/jobCreators'
import { jobCreators } from '../behavior/jobCreators-sidecar'
import { IJob } from '../behavior/jobs'
import { IFrameAnim } from '../engine/anims'
import { anims } from '../engine/anims-sidecar'
import { cargoes } from '../entities/cargoes-sidecar'
import { IEntity } from '../entities/entitiesUtils'
import { entitiesUtils } from '../entities/entitiesUtils-sidecar'
import { IFullnessIndicator } from '../indicators/fullnessIndicators'
import { assert } from '../lib/assert-sidecar'
import { distance } from '../lib/distance-sidecar'
import { l } from '../lib/imports'
import { createId } from '../main/common'
import { layers } from '../main/layers-sidecar'
import { scene } from '../main/scene-sidecar'
import { spriteMap } from '../main/spriteMap-sidecar'
import { IMonster } from '../monsters/monsters'
import { monsters } from '../monsters/monsters-sidecar'
import { ITileMapShip } from '../tiles/tileMapShip'
import { machinesAnims } from './machinesAnims-sidecar'
import { IMachineData, IMachinePartData, IMachineType } from './machinesData'
import { machinesData } from './machinesData-sidecar'
import { machinesGrower } from './machinesGrower-sidecar'
import { machinesIceMelter } from './machinesIceMelter-sidecar'
import { machinesOxygenGenerator } from './machinesOxygenGenerator-sidecar'
import { machinesReactor } from './machinesReactor-sidecar'
import { machinesWaterCooler } from './machinesWaterCooler-sidecar'
import { machinesOxygenGeneratorEm } from './machinesOxygenGeneratorEm-sidecar'
import { machinesDoor } from './machinesDoor-sidecar'
import { machinesAirlock2 } from './machinesAirlock2-sidecar'
import { ICargo } from '../entities/cargoes'
import { ICargoType } from '../entities/cargoesData'
import { stacks } from '../entities/stacks-sidecar'
import { machinesPile } from './machinesPile-sidecar'
import { IStevieText } from '../uiParts/stevieText'
import { stevieText } from '../uiParts/stevieText-sidecar'
import { machinesBed } from './machinesBed-sidecar'
import { IProgressIndicator } from '../indicators/progressIndicators'
import { progressIndicators } from '../indicators/progressIndicators-sidecar'
import { fullnessIndicators } from '../indicators/fullnessIndicators-sidecar'

export interface IMachinePart extends IEntity {
  parent: IMachine
  data: IMachinePartData
  i: number
  j: number
  image: Phaser.GameObjects.Image
  anim: IFrameAnim
}

export interface IMachine extends IEntity {
  data: IMachineData
  image: Phaser.GameObjects.Image
  imageGhost: Phaser.GameObjects.Image | null
  i: number
  j: number

  isOpen: boolean
  emergencySupply: number
  waterSupply: number
  batterySupply: number
  hydrogenSupply: number
  fullnessIndicator: IFullnessIndicator | null
  progressIndicator: IProgressIndicator | null
  progress: number
  hasSuit: boolean
  isOccupied: boolean
  jobs: IJob[]
  jobCreators: IJobCreator[]

  cargoes: ICargo[]
  holdsType: ICargoType

  anim: IFrameAnim

  parts: IMachinePart[]

  text: IStevieText

  onUpdate: (obj: IMachine) => void
}

const noopUpdate = () => {}

const objs: IMachine[] = []
export function getAll() {
  return objs
}

const objsStacks: IMachine[] = []
export function getAllStacks() {
  return objsStacks
}

export function findById(id: number) {
  return entitiesUtils.findById(id, objs)
}

export function ensureType(obj: IMachine, type: IMachineType) {
  assert.eq(
    obj.data.type,
    type,
    'wrong machine type!',
    'expected',
    type,
    'got',
    obj.data.type,
  )
}

export function findClosestTo(
  i: number,
  j: number,
  filter: (c: IMachine) => boolean,
  invalid: IMachine[] = [],
) {
  return distance.findClosestTo(objs, i, j, filter, invalid)
}

export function findAtSpot(
  i: number,
  j: number,
  filter: (c: IMachine) => boolean,
): IMachine | null {
  let closest: IMachine | null = null
  l.forEach(objs, (c) => {
    if (c.i === i && c.j === j) {
      if (filter(c)) {
        closest = c
        return false
      }
    }
    l.forEach(c.parts, (part) => {
      if (part.i === i && part.j === j) {
        if (filter(c)) {
          closest = c
          return false
        }
      }
    })
  })
  return closest
}

export function findPartAtSpot(
  i: number,
  j: number,
  filter: (c: IMachine) => boolean,
): IMachinePart | null {
  let closest: IMachinePart | null = null
  l.forEach(objs, (c) => {
    l.forEach(c.parts, (part) => {
      if (part.i === i && part.j === j) {
        if (filter(c)) {
          closest = part
          return false
        }
      }
    })
  })
  return closest
}

function createPart(obj: IMachine, idx: number) {
  let id = createId()
  assert.notNil(obj.data.parts)
  let data = obj.data.parts[idx]
  assert.notNil(data)

  let image = spriteMap.createImageFromFrameInfo(
    layers.getLayers().machineParts,
    data.frames[0],
    0,
    0,
  )
  if (data.isDefaultBacking) {
    image.alpha = 0.5
  }

  let anim = anims.create(machinesAnims.all.reactorGo, image)

  let part: IMachinePart = {
    id,
    eType: 'machine-part',
    data,
    parent: obj,
    i: obj.i + data.oi,
    j: obj.j + data.oj,
    isDead: false,
    image,
    anim,
  }

  obj.parts.push(part)
}

export function create(type: IMachineType): IMachine {
  let id = createId()
  let data = machinesData.dataMapName[type]

  let image = spriteMap.createImageFromFrameInfo(
    layers.getLayers().machines,
    data.frames[0],
    0,
    0,
  )

  let ctx = scene.getContext()

  let fullnessIndicator: IFullnessIndicator | null = null
  let progressIndicator: IProgressIndicator | null = null
  let anim = anims.create(machinesAnims.all.reactorGo)
  let text = stevieText.create(ctx.font16, 0, 0, layers.getLayers().machines)
  stevieText.setText(text, '' + data.type)

  let obj: IMachine = {
    data,
    eType: 'machine',
    id,
    image,
    imageGhost: null,
    isDead: false,
    i: -1,
    j: -1,
    anim,
    isOpen: false,
    emergencySupply: 0,
    waterSupply: 0,
    batterySupply: 0,
    hydrogenSupply: 0,
    jobs: [],
    jobCreators: [],
    fullnessIndicator,
    progressIndicator,
    progress: 0,
    hasSuit: type === 'suits',
    isOccupied: false,
    parts: [],
    cargoes: [],
    holdsType: 'error',
    onUpdate: noopUpdate,
    text,
  }

  if (data.parts) {
    image.visible = false
    for (let i = 0; i < data.parts.length; i++) {
      createPart(obj, i)
    }
  }

  if (type === 'pile') {
    machinesPile.onCreate(obj)
  }
  if (type === 'bed') {
    machinesBed.onCreate(obj)
  }
  if (type === 'ice-melter') {
    machinesIceMelter.onCreate(obj)
  }
  if (obj.data.type === 'reactor-em') {
    machinesReactor.onCreate(obj)
  }
  if (obj.data.type === 'o2gen') {
    machinesOxygenGenerator.onCreate(obj)
  }
  if (obj.data.type === 'o2gen-em') {
    machinesOxygenGeneratorEm.onCreate(obj)
  }
  if (type === 'water-cooler') {
    machinesWaterCooler.onCreate(obj)
  }
  if (type === 'grower') {
    machinesGrower.onCreate(obj)
  }
  if (type === 'door') {
    machinesDoor.onCreate(obj)
  }
  if (type === 'airlock2') {
    machinesAirlock2.onCreate(obj)
  }
  objs.push(obj)
  if (obj.data.isStack) {
    objsStacks.push(obj)
  }

  return obj
}

export function moveToTile(
  obj: IMachine,
  map: ITileMapShip,
  i: number,
  j: number,
) {
  obj.i = i
  obj.j = j
  obj.image.x = map.ox + i * map.tileSize
  obj.image.y = map.oy + j * map.tileSize

  if (obj.imageGhost) {
    obj.imageGhost.x = map.ox + i * map.tileSize
    obj.imageGhost.y = map.oy + j * map.tileSize
  }

  for (let idx = 0; idx < obj.parts.length; idx++) {
    let p = obj.parts[idx]
    p.i = i + p.data.oi
    p.j = j + p.data.oj
    p.image.x = map.ox + p.i * map.tileSize
    p.image.y = map.oy + p.j * map.tileSize
  }
  // console.log(
  //   'move to',
  //   obj.data.upperLeftOi,
  //   obj.data.upperLeftOj,
  //   obj.data.type,
  // )
  stevieText.moveTo(
    obj.text,
    map.ox + (i + obj.data.upperLeftOi) * map.tileSize - 10,
    map.oy + (j + obj.data.upperLeftOj) * map.tileSize - 14,
  )
}

export function killAll() {
  objs.forEach((c) => {
    kill(c)
  })
}

export function kill(obj: IMachine) {
  if (!obj.isDead) {
    obj.isDead = true

    for (let i = obj.cargoes.length - 1; i >= 0; i--) {
      let c = obj.cargoes[i]
      stacks.removeFromStack(obj, c)
    }

    obj.cargoes = []
  }
}

export function updateAll() {
  objs.forEach((c) => {
    update(c)
  })
  entitiesUtils.removeDead(objs, (c) => {
    c.image.destroy()
    l.forEach(c.parts, (d) => {
      d.image.destroy()
    })
    if (c.imageGhost) {
      c.imageGhost.destroy()
    }
    stevieText.kill(c.text)
    if (c.progressIndicator) {
      progressIndicators.kill(c.progressIndicator)
    }
    if (c.fullnessIndicator) {
      fullnessIndicators.kill(c.fullnessIndicator)
    }
  })
  entitiesUtils.removeDead(objsStacks, (c) => {})
}
function update(obj: IMachine) {
  let ctx = scene.getContext()

  // Sanity check
  l.forEach(obj.cargoes, (c) => {
    assert.notNil(c)
  })

  stevieText.setVisible(obj.text, false)

  obj.jobs = jobCreators.pruneJobs(obj.jobs)

  if (obj.jobs.length === 0) {
    let j = jobCreators.runMulti(obj.jobCreators)
    if (j) {
      obj.jobs.push(j)
    }
  }

  obj.onUpdate(obj)
}

export function harvest(obj: IMachine, m: IMonster) {
  assert.eq(obj.data.type, 'grower', 'not grower')
  assert.t(obj.progress >= 100, 'not enough progress')

  let ctx = scene.getContext()

  let c = cargoes.create('food')
  cargoes.moveToTile(c, ctx.mapShip, obj.i, obj.j)
  monsters.pickupCargo(m, c)
  obj.progress = 0
  obj.waterSupply = 0
}

export function setHoldsType(obj: IMachine, cargoType: ICargoType) {
  obj.holdsType = cargoType
}

export function setProgress(obj: IMachine, progress: number, max: number) {
  if (!obj.progressIndicator) {
    obj.progressIndicator = progressIndicators.create()
  }
  progressIndicators.attach(
    obj.progressIndicator,
    obj.image.x,
    obj.image.y,
    progress / max,
  )
}
export function clearProgress(obj: IMachine) {
  if (obj.progressIndicator) {
    progressIndicators.kill(obj.progressIndicator)
    obj.progressIndicator = null
  }
}
